import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    id: "landscaping",
    title: "Landscape Installation & Maintenance",
    description: "Transform your property with professional landscape design and ongoing maintenance services. From new installations to seasonal care, we create and maintain beautiful outdoor spaces.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/cz3w10y4_Screenshot%202026-02-25%20at%203.46.31%E2%80%AFPM.png",
    alt: "Residential landscape installation in Joliet IL",
    features: [
      "Custom landscape design",
      "Lawn installation and renovation",
      "Shrub and tree planting",
      "Mulching and bed maintenance",
      "Seasonal cleanups",
      "Irrigation system installation"
    ],
    areas: "Joliet, New Lenox, Frankfort, Naperville, and surrounding areas"
  },
  {
    id: "pavers",
    title: "Paver Patios, Walkways & Driveways",
    description: "Enhance your outdoor living with stunning paver installations. We create durable, beautiful patios, walkways, and driveways using premium materials that last for years.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/dx0a3wdq_IMG_4852.jpeg",
    alt: "Paver patio contractor in New Lenox IL",
    features: [
      "3D custom patio design",
      "Interlocking paver driveways",
      "Stone walkways and paths",
      "Pool deck installations",
      "Paver repair and restoration",
      "Pattern and color consultation"
    ],
    areas: "New Lenox, Orland Park, Homer Glen, Naperville, and Will County"
  },
  {
    id: "retaining-walls",
    title: "Retaining Walls",
    description: "Professional retaining wall construction for both functional and decorative purposes. We build walls that prevent erosion, create usable space, and add visual appeal to your landscape.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/ja35jvxr_Screenshot%202026-02-25%20at%203.46.46%E2%80%AFPM.png",
    alt: "Retaining wall installation in Frankfort IL",
    features: [
      "Structural retaining walls",
      "Decorative garden walls",
      "Natural stone walls",
      "Block wall systems",
      "Terracing and leveling",
      "Drainage solutions"
    ],
    areas: "Frankfort, Manhattan, Mokena, Joliet, and surrounding Illinois communities"
  },
  {
    id: "concrete",
    title: "Concrete Services",
    description: "Quality concrete work for driveways, patios, sidewalks, and more. Our skilled team delivers professional flatwork with excellent finishes that stand the test of time.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/4kmk778h_Screenshot%202026-02-25%20at%203.47.03%E2%80%AFPM.png",
    alt: "Concrete driveway contractor in Naperville IL",
    features: [
      "Concrete driveways",
      "Patio slabs",
      "Sidewalks and pathways",
      "Stamped concrete",
      "Concrete repair"
    ],
    areas: "Naperville, Aurora, Plainfield, Joliet, and greater Will County"
  },
  {
    id: "snow-removal",
    title: "Snow & Ice Management",
    description: "Keep your property safe and accessible all winter with our reliable snow and ice management services. We offer residential and commercial snow removal throughout the season.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/1l72nh65_IMG_0205.jpeg",
    alt: "Snow removal services in Will County IL",
    features: [
      "Residential snow plowing",
      "Commercial snow removal",
      "Sidewalk clearing",
      "Ice treatment and salting",
      "24/7 emergency service",
      "Seasonal contracts available"
    ],
    areas: "Joliet, Shorewood, Morris, New Lenox, and all of Will County, Illinois"
  }
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Page Header */}
      <section className="bg-[#F9FAFB] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#2F5930] font-semibold mb-3 tracking-wide uppercase text-sm">
              Our Services
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-6 font-['Manrope']">
              Professional Landscaping Services in Illinois
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              From landscape installation to snow removal, J Guzman Landscaping LLC provides 
              comprehensive outdoor services for residential and commercial properties throughout 
              Joliet, IL and surrounding areas.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <article 
                key={service.id}
                id={service.id}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`service-section-${service.id}`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="relative rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={service.image}
                      alt={service.alt}
                      className="w-full aspect-[16/10] object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>

                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-4 font-['Manrope']">
                    {service.title}
                  </h2>
                  <p className="text-lg text-[#4B5563] mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#2F5930] flex-shrink-0" />
                        <span className="text-[#4B5563]">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <p className="text-sm text-[#9CA3AF] mb-6">
                    <strong className="text-[#4B5563]">Serving:</strong> {service.areas}
                  </p>

                  <div className="flex flex-wrap gap-4">
                    <Link to="/contact">
                      <Button 
                        className="bg-[#2F5930] hover:bg-[#244525] text-white rounded-full px-6"
                        data-testid={`service-cta-${service.id}`}
                      >
                        Get Free Estimate
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <a href="tel:8159098790">
                      <Button 
                        variant="outline"
                        className="text-[#2F5930] border-[#2F5930] hover:bg-[#F0FDF4] rounded-full px-6"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Call Now
                      </Button>
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#2F5930] py-16 md:py-20" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Manrope']">
            Need a Custom Landscaping Solution?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Every property is unique. Contact us to discuss your specific landscaping needs 
            and get a personalized quote.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:8159098790">
              <Button 
                size="lg"
                className="bg-white text-[#2F5930] hover:bg-gray-100 rounded-full px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                (815) 909-8790
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-2 border-white hover:bg-white/10 rounded-full px-8"
              >
                Request Free Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
